'use strict';

(function () {
	const resolve = x => ({
		on: () => resolve(x),
		otherwise: () => x
	});

	/**
	 * @template T, R
	 * @typedef {object} Matcher
	 * @property {(cond: (x: T) => boolean, fn: (x: T) => R) => Matcher<T, R>} on
	 * @property {(fn: (x: T) => R) => R} otherwise
	 */

	/**
	 * Functional switch case utility
	 *
	 * Add predicate functions to check if value is "truthie" it will
	 * proceed calling the second function otherwise it will proceed to
	 * the next `.on` until it matches or hits the `.otherwise case`
	 *
	 * @example
	 * match(value)
	 * 	.on(predicateFn, matchFn)
	 * 	.on(otherPredicateFn, otherMatchFn)
	 * 	.otherwise(onNoMatchesFn)
	 *
	 *
	 * @template T, R
	 * @param {T} x Value to match against
	 * @returns {Matcher<T, R>}
	 */
	const match = x => ({
		on: (cond, fn) => (cond(x) ? resolve(fn(x)) : match(x)),
		otherwise: fn => fn(x)
	});

	/**
	 * Safe getter for objects
	 *
	 * Innermost value is returned when full path exist,
	 * but if the path is broken null will be returned.
	 *
	 * @example
	 * 		getByPath(['keyA', 'keyB'], { keyA: { keyB: 0 } })
	 * 		// returns 0
	 *
	 * 		getByPath(['keyA', 'keyB'], { keyA: { keyB: 'Value' } })
	 * 		// returns 'Value'
	 *
	 * 		getByPath(['keyA', 'keyB', 'keyC'], { keyA: {keyB: undefined } })
	 * 		// returns null
	 *
	 * @param {array} p Path
	 * @param {object} o Object
	 * @return {*}
	 */
	const getByPath = (p, o) => {
		const innerMostPath = p.slice(-1)[0];
		return p.reduce(
			(xs, x) =>
				(xs && xs[x] !== undefined && x === innerMostPath) || (xs && xs[x]) ? xs[x] : null,
			o
		);
	};

	const groupsOf = (array, sliceSize) => {
		const sliced = array.reduce(
			(acc, x) => {
				const last = () => acc.slice(-1)[0];
				if (last().length >= sliceSize) {
					acc.push([]);
				}
				last().push(x);
				return acc;
			},
			[[]]
		);

		return sliced.filter(x => x.length > 0);
	};

	const getCurrentUnixTimeStamp = ({ format }) => {
		const msTimeStamp = () => new Date().getTime();
		const secTimeStamp = () => Math.round(msTimeStamp() / 1000);

		return match(format)
			.on(x => ['sec', 'seconds'].includes(x), secTimeStamp)
			.on(x => ['ms', 'milliseconds'].includes(x), msTimeStamp)
			.otherwise(msTimeStamp);
	};

	/**
	 * First we use encodeURIComponent to get percent-encoded UTF-8, then we convert
	 * the percent encodings into raw bytes which can be fed into btoa.
	 *
	 * @param btoa
	 * @param str
	 * @returns {string}
	 */
	const b64EncodeUnicode = (btoaFn, str) => {
		const toSolidBytes = (fullMatch, p1) => String.fromCharCode('0x' + p1);
		return btoaFn(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, toSolidBytes));
	};

	const csvDownloadStream = btoaFn => csvString =>
		'data:application/octet-stream;base64,' + b64EncodeUnicode(btoaFn, csvString);

	const xlsxDownloadStream = btoaFn => xlsxString =>
		'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' +
		b64EncodeUnicode(btoaFn, xlsxString);

	module.exports = {
		getByPath: getByPath,
		match: match,
		groupsOf: groupsOf,
		csvDownloadStream: csvDownloadStream,
		xlsxDownloadStream: xlsxDownloadStream,
		getCurrentUnixTimeStamp: getCurrentUnixTimeStamp
	};
})(typeof module !== 'undefined' && module.exports);
